import { AppDebug, AppDebugLogType } from "../app/debug/AppDebug";
import { IInteractive } from "../interfaces/IInteractive";
import { ScreenManager } from "../managers/ScreenManager";
import { TextureStatesData } from "../types/types";
import { GetBounds } from "../utils/GetBounds";


export enum ButtonEvents {
    /**
     * Button was clicked
     * > ButtonDown was registered and ButtonUp occured without ButtonOut occuring
     */
    CLICK = "click",
}

/**
 * Button with enable/disable functionality and 3 different states.
 * 
 * Visual + interactivity states:
 * > default = out | up + enabled
 * > over | down + enabled
 * > default + disabled (disabled used default/out state)
 * 
 * use-case 
 * execution order
 * myButton
 *  .InitBase()
 *  .PostInit()
 */
export class ButtonBase
    extends Phaser.GameObjects.Container
    implements IInteractive {
    /**
     * Displays current state texture (up | over | disabled)
     */
    public view: Phaser.GameObjects.Image;

    /**
     * Texture data for all button states
     */
    protected buttonTextureDatas: TextureStatesData;

    /**
     * Flag that reports whether button is enabled or disabled
     */
    protected isInteractive: boolean;

    protected timeOut: Phaser.Time.TimerEvent;
    protected timeOutDelay: number = 0;

    protected cachedHitArea: any;
    protected cachedHitAreaCallback: Phaser.Types.Input.HitAreaCallback;

    /**
     * Extrusion applied to hitArea @see PostInit
     * NOTE: only applied to @see Phaser.Geom.Rectangle hitArea
     */
    protected hitAreaExtrusion: number;

    /**
     * Was Button downed
     * Helper for CLICK calculation
     */
    protected isDown: boolean = false;
    /**
     * Allows us to (in case of toggles for ex) set state after toggle to 'over', not 'out'
     */
    protected isOver: boolean = false;


    /**
     * Initializes the base button
     * 
     * @param buttonTextureDatas composite of all states texture data (out, over, disabled)
     * NOTE: if @see buttonTextureDatas.over is set to null, 
     *       Button wont have the 'over texture' or 'mouse over' behavior
     * NOTE: Button needs at least an @see buttonTextureDatas.out texture! Make sure to provide it!
     * @param isInteractive Should button by default be in its enabled state, or disabled state?
     *      default: true
     * @param timeOutDelay 
     * @returns 
     */
    public InitBase(
        buttonTextureDatas: TextureStatesData,
        isInteractive: boolean = true,
        viewScale: number = 1,
        timeOutDelay: number = 0,
    ): void {
        this.buttonTextureDatas = buttonTextureDatas;
        this.isInteractive = isInteractive;
        this.timeOutDelay = timeOutDelay;

        this.view = this.scene.add.image(0, 0,
            buttonTextureDatas.out.texture,
            buttonTextureDatas.out.frame
        );
        this.view.setScale(viewScale);
        this.add(this.view);

        // set hitArea size
        this.setSize(this.view.displayWidth, this.view.displayHeight);
    }

    /**
     * Call postInit after initializing all subclasses
     * 
     * @param hitAreaExtrusion When using Rectangular hitArea, extrude the default rect size by this amount
     */
    public PostInit(hitAreaExtrusion: number = 0): void {
        this.hitAreaExtrusion = hitAreaExtrusion;

        if (this.buttonTextureDatas.over != null) {
            this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, this.OnPointerOver);
            this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, this.OnPointerOut);
        }
        this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.OnPointerDown);
        this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.OnPointerUp);

        this.SetInteractive(this.isInteractive, true);
        this.UpdateHitArea();
    }

    protected UpdateHitArea(): void {
        this.cachedHitArea = GetBounds(this.view, this.hitAreaExtrusion);
        this.cachedHitAreaCallback = Phaser.Geom.Rectangle.Contains;

        if (this.input != null) {
            this.input.hitArea = this.cachedHitArea;
            this.input.hitAreaCallback = this.cachedHitAreaCallback;
        }
    }

    /**
     * Set button's base texture states
     * @param buttonTextureDatas 
     */
    public SetButtonTextureDatas(buttonTextureDatas: TextureStatesData, updateView: boolean = false): void {
        this.buttonTextureDatas = buttonTextureDatas;

        if (updateView) {
            this.UpdateView();
        }
    }

    /**
     * Update view from state
     */
    public UpdateView(): void {
        this.UpdateViewFromInteractive();
    }

    /**
     * Set custom Rectangle hitArea
     * @param area custom Rectangle hitArea
     */
    public SetRectangleHitArea(area: Phaser.Geom.Rectangle): void {
        this.cachedHitArea = area;
        this.cachedHitAreaCallback = Phaser.Geom.Rectangle.Contains;

        if (this.input != null) {
            this.input.hitArea = this.cachedHitArea;
            this.input.hitAreaCallback = this.cachedHitAreaCallback;
        }
    }

    /**
     * Custom interactivity handling for this button.
     * Wrapper for @see setInteractive and @see disableInteractive
     * @param interactive True:  GameObject is set to be interactive (receives mouse/touch/.. events)
     *                    False: GameObject is set to not be interactive
     * @param force forces setting this interactive value
     */
    public SetInteractive(interactive: boolean, force: boolean = false): void {
        if ((this.isInteractive != interactive) || force) {
            if (interactive) {
                this.setInteractive({
                    // hitArea: GetBounds(this.view),
                    // hitAreaCallback: Phaser.Geom.Rectangle.Contains,
                    useHandCursor: true
                });
                if (this.cachedHitArea != undefined) {
                    this.input.hitArea = this.cachedHitArea;
                }
                if (this.cachedHitAreaCallback != undefined) {
                    this.input.hitAreaCallback = this.cachedHitAreaCallback;
                }
            }
            else {
                this.disableInteractive();
            }
            this.isInteractive = interactive;
            this.UpdateViewFromInteractive();
        }
    }

    public IsInteractive(): boolean {
        return this.isInteractive;
    }

    /**
     * Update view from change in state
     */
    protected UpdateViewFromInteractive(): void {
        // was disabled, is enabled
        if (this.isInteractive) {
            // was using disabled texture
            if (this.buttonTextureDatas.disabled != null) {
                // set 'out' texture
                this.view.setTexture(this.buttonTextureDatas.out.texture, this.buttonTextureDatas.out.frame);
            }
        }
        // was enabled, is disabled
        else {
            // has disabled texture
            if (this.buttonTextureDatas.disabled != null) {
                // set disabled texture
                this.view.setTexture(this.buttonTextureDatas.disabled.texture, this.buttonTextureDatas.disabled.frame);
            }
        }
    }

    protected OnPointerOver(): void {
        this.isOver = true;
        if (this.isInteractive) {
            if (this.buttonTextureDatas.over != null) {
                this.view.setTexture(this.buttonTextureDatas.over.texture, this.buttonTextureDatas.over.frame);
            }
        }
    }

    protected OnPointerOut(): void {
        this.isOver = false;
        this.isDown = false;

        if (this.isInteractive) {
            if (this.buttonTextureDatas.out != null) {
                this.view.setTexture(this.buttonTextureDatas.out.texture, this.buttonTextureDatas.out.frame);
            }
        }
    }

    protected OnPointerDown(): void {
        this.isDown = true;
    }

    protected OnPointerUp(): void {
        if (this.isDown) {
            this.emit(ButtonEvents.CLICK);
            this.isDown = false;
        }

        if (this.timeOutDelay > 0) {
            this.TimeOutDisable();
        }
    }

    /**
     * Disable button for @see this.timeOutDelay
     */
    public TimeOutDisable(): void {
        this.SetInteractive(false);
        this.timeOut = this.scene.time.delayedCall(this.timeOutDelay, this.OnTimeOut, undefined, this);
    }

    protected OnTimeOut(): void {
        if (this.timeOutDelay > 0) {
            this.SetInteractive(true);
        }

        const thisWorldTM: Phaser.GameObjects.Components.TransformMatrix = this.getWorldTransformMatrix();
        const cursorOverButton: boolean = (this.input.hitArea as Phaser.Geom.Rectangle).contains(
            this.scene.input.mousePointer.x - thisWorldTM.tx + this.displayOriginX,
            this.scene.input.mousePointer.y - thisWorldTM.ty + this.displayOriginY
        );
        cursorOverButton ? this.OnPointerOver() : this.OnPointerOut();
    }

    public override destroy(): void {
        this.off(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, this.OnPointerOver);
        this.off(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, this.OnPointerOut);
        this.off(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.OnPointerDown);
        this.off(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.OnPointerUp);

        if (this.timeOut != undefined) {
            this.timeOut.remove();
            this.timeOut.destroy();
        }

        this.view.destroy();

        this.timeOut = null;
        this.view = null;
        this.buttonTextureDatas = null;
    }
}